import React, { useCallback, useEffect, useRef, useState } from "react";
import { oracleDiagApi } from "@/api/adminOneClient";

/**
 * CareLogix™ ORACLE — AdminOne · Diagnostics (Phase 16.5).
 * Live health dashboard for the public ORACLE platform. Polls
 * /api/oracle/diagnostics every 5 seconds per Constitutional Pack v1.5.0.
 *
 * Strictly additive. Never modifies any other AdminOne page. Read-only.
 */
const REFRESH_MS = 5000;

const ROW_DEFS = [
  { key: "backend",        label: "Backend (FastAPI)",      good: ["online"] },
  { key: "mongo",          label: "MongoDB",                good: ["connected"] },
  { key: "llm",            label: "LLM Engine",             good: ["configured"], warn: ["degraded"], bad: ["missing"] },
  { key: "model",          label: "Primary Model",          info: true },
  { key: "fallback_model", label: "Fallback Model",         info: true },
  { key: "oanda",          label: "OANDA Live Feed",        good: ["configured"], warn: ["missing"] },
  { key: "pdf",            label: "PDF Dossier Engine",     good: ["ready"] },
  { key: "adminone",       label: "AdminOne Command Center", good: ["ready"] },
  { key: "version",        label: "Platform Version",       info: true },
];

function classifyValue(def, value) {
  if (def.info) return "info";
  const v = String(value || "").toLowerCase();
  if ((def.good || []).includes(v)) return "ok";
  if ((def.warn || []).includes(v)) return "warn";
  if ((def.bad  || []).includes(v)) return "bad";
  if (!value) return "warn";
  return "ok";
}

function StatusDot({ tone }) {
  return <span className={`adiag-dot adiag-dot--${tone}`} aria-hidden="true" />;
}

export default function AdminDiagnostics() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [refreshing, setRefreshing] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const intervalRef = useRef(null);

  const load = useCallback(async () => {
    setRefreshing(true);
    try {
      const { data: r } = await oracleDiagApi.diagnostics();
      setData(r);
      setLastUpdated(new Date());
      setErr("");
    } catch (e) {
      setErr(e?.message || "Failed to fetch diagnostics.");
    } finally {
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    load();
    if (!autoRefresh) return undefined;
    intervalRef.current = setInterval(load, REFRESH_MS);
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [load, autoRefresh]);

  const llmTone = data ? classifyValue({ key: "llm", good: ["configured"], warn: ["degraded"], bad: ["missing"] }, data.llm) : "info";
  const overall = !data ? "warn" : (data.llm === "missing" || data.mongo === "disconnected") ? "bad"
                : (data.llm === "degraded" || data.oanda === "missing") ? "warn"
                : "ok";

  return (
    <div className="admin-page" data-testid="admin-diagnostics">
      <header className="admin-page__hd">
        <div>
          <h1 className="admin-page__title">ORACLE Diagnostics</h1>
          <p className="admin-page__sub">
            Live constitutional health monitor · Auto-refresh every {REFRESH_MS / 1000}s · LOGIX-2™ · GIOS™
          </p>
        </div>
        <div className="admin-page__badges">
          <span className={`admin-badge ${overall === "ok" ? "is-on" : overall === "warn" ? "is-warn" : "is-off"}`} data-testid="admin-diagnostics-overall">
            {overall === "ok" ? "✅ ALL SYSTEMS NOMINAL" : overall === "warn" ? "⚠️ DEGRADED" : "🛑 CRITICAL"}
          </span>
          <button
            type="button"
            className={`admin-pill ${autoRefresh ? "is-active" : ""}`}
            onClick={() => setAutoRefresh((v) => !v)}
            data-testid="admin-diagnostics-toggle-auto"
            title="Toggle 5-second auto-refresh"
          >
            {autoRefresh ? "● AUTO-REFRESH ON" : "○ AUTO-REFRESH OFF"}
          </button>
          <button
            type="button"
            className="admin-btn admin-btn--ghost"
            onClick={load}
            disabled={refreshing}
            data-testid="admin-diagnostics-refresh"
          >
            {refreshing ? "Refreshing…" : "↻ Refresh now"}
          </button>
        </div>
      </header>

      {err && <div className="admin-card admin-card--bad" data-testid="admin-diagnostics-error">{err}</div>}

      {/* Hero summary cards */}
      <section className="admin-grid admin-grid--4" data-testid="admin-diagnostics-hero">
        <div className="admin-card adiag-hero">
          <div className="admin-card__label">Backend</div>
          <div className="admin-card__value">
            <StatusDot tone={classifyValue(ROW_DEFS[0], data?.backend)} /> {data?.backend || "—"}
          </div>
        </div>
        <div className="admin-card adiag-hero">
          <div className="admin-card__label">MongoDB</div>
          <div className="admin-card__value">
            <StatusDot tone={classifyValue(ROW_DEFS[1], data?.mongo)} /> {data?.mongo || "—"}
          </div>
        </div>
        <div className="admin-card adiag-hero">
          <div className="admin-card__label">LLM Reasoning</div>
          <div className="admin-card__value">
            <StatusDot tone={llmTone} /> {data?.llm || "—"}
          </div>
          <div className="adiag-sub">
            {data?.model ? `${data.model}` : "—"}
            {data?.fallback_model ? <span style={{ opacity: 0.6 }}> · fallback {data.fallback_model}</span> : null}
          </div>
        </div>
        <div className="admin-card adiag-hero">
          <div className="admin-card__label">OANDA Feed</div>
          <div className="admin-card__value">
            <StatusDot tone={classifyValue(ROW_DEFS[5], data?.oanda)} /> {data?.oanda || "—"}
          </div>
          <div className="adiag-sub">
            {data?.oanda === "configured" ? "Live FX data online" : "Simulated context only (no live keys)"}
          </div>
        </div>
      </section>

      {/* Full status table */}
      <section className="admin-section" data-testid="admin-diagnostics-table">
        <h2 className="admin-section__h">Full Constitutional Status</h2>
        <table className="admin-table">
          <thead>
            <tr>
              <th>Component</th>
              <th>State</th>
              <th>Detail</th>
            </tr>
          </thead>
          <tbody>
            {ROW_DEFS.map((def) => {
              const v = data?.[def.key];
              const tone = classifyValue(def, v);
              return (
                <tr key={def.key} data-testid={`admin-diagnostics-row-${def.key}`}>
                  <td><b>{def.label}</b></td>
                  <td>
                    {def.info ? (
                      <span className="admin-tag">{v || "—"}</span>
                    ) : (
                      <span className={`admin-tag admin-tag--${tone}`}>
                        {String(v || "unknown").toUpperCase()}
                      </span>
                    )}
                  </td>
                  <td style={{ fontSize: 12, color: "rgba(212,197,160,.65)" }}>
                    {detailFor(def.key, data)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </section>

      {/* Brand strip */}
      <section className="admin-section" data-testid="admin-diagnostics-brand">
        <div className="adiag-brand">
          <div>
            <div className="adiag-brand__title">Powered by {data?.powered_by || "LOGIX-2™"}</div>
            <div className="adiag-brand__sub">{data?.operating_system || "Global Intelligence Operating System™ (GIOS™)"}</div>
          </div>
          {lastUpdated && (
            <div className="adiag-brand__updated" data-testid="admin-diagnostics-last-updated">
              Last updated {lastUpdated.toLocaleTimeString()}
              {refreshing && <span style={{ marginLeft: 8, color: "#e8a020" }}>· refreshing…</span>}
            </div>
          )}
        </div>
      </section>

      {/* Operational guidance */}
      <section className="admin-section" data-testid="admin-diagnostics-guidance">
        <h2 className="admin-section__h">Operational Guidance</h2>
        <ul className="adiag-guidance">
          <li>
            <b>LLM degraded?</b> The reasoning engine is auto-falling back to <code>{data?.fallback_model || "claude-3-5-sonnet-latest"}</code>.
            Recent failures persist in the chyron as <i>ORACLE REASONING ENGINE DEGRADED · RETRY ADVISED</i> for 5 minutes.
          </li>
          <li>
            <b>Mongo disconnected?</b> Sessions, audit log, and signals will pause. Restart the backend or check the Mongo cluster URL in <code>backend/.env</code>.
          </li>
          <li>
            <b>OANDA missing?</b> OANDA chat returns <i>simulated/public</i> context with a NEUTRAL verdict — no 502 to users. Wire OANDA keys to enable live data.
          </li>
          <li>
            <b>No 502 to end users</b> is constitutional. Even when upstream is hard down, <code>/api/oracle/chat</code> returns a graceful 200 envelope.
          </li>
        </ul>
      </section>
    </div>
  );
}

function detailFor(key, data) {
  if (!data) return "—";
  switch (key) {
    case "backend":        return "FastAPI worker is accepting requests.";
    case "mongo":          return data.mongo === "connected" ? "Sessions, audit log, signals all writable." : "Persistence layer offline.";
    case "llm":            return data.llm === "configured" ? "EMERGENT_LLM_KEY active." : data.llm === "degraded" ? "Recent upstream failure in last 10m — fallback in use." : "No EMERGENT_LLM_KEY or ANTHROPIC_API_KEY configured.";
    case "model":          return "Primary Anthropic model requested per ANTHROPIC_MODEL env.";
    case "fallback_model": return "Used automatically on primary-model failure.";
    case "oanda":          return data.oanda === "configured" ? "Live G10 FX data routed through OANDA v20 proxy." : "Live keys not configured. Simulated context served.";
    case "pdf":            return "Dossier exports (cover, executive summary, watchlist) ready.";
    case "adminone":       return "AdminOne Command Center mounted at /api/adminone.";
    case "version":        return data.version || "v1.5.0";
    default:               return "—";
  }
}
